<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiField from '@/components/pos-v2/ui/PosUiField.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSwitch from '@/components/pos-v2/ui/PosUiSwitch.vue';
  import PosUiTextarea from '@/components/pos-v2/ui/PosUiTextarea.vue';
  import { useMenuImport } from '@/composition/menu-import/menu-import-context';
  import { useDraftActions } from '@/composition/menu-import/use-draft-actions';
  import {
    useDishFields,
    useOptimisticValue,
  } from '@/composition/menu-import/use-dish-fields';
  import { useModal } from '@/composition/use-modal';
  import {
    ADDON_GROUP_MODAL,
    DRAFT_DESCRIPTION_MAX,
    groupUsage,
    referencePrice,
  } from '@/domain/menu-import/item-editor';
  import type { TDishField } from '@/domain/menu-import/item-editor';
  import {
    addonRuleKey,
    orderedCategories,
  } from '@/domain/menu-import/menu-draft';
  import { DRAFT_NAME_MAX } from '@/domain/menu-import/workspace-view';
  import type {
    IDraftAddonGroup,
    IDraftItem,
    IDraftPatchResult,
    IMenuDraft,
    TDraftOp,
  } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';
  import V2MenuAddonGroupModal from '../editor/V2MenuAddonGroupModal.vue';
  import V2MenuAllergenChips from '../editor/V2MenuAllergenChips.vue';
  import V2MenuCategorySelect from '../editor/V2MenuCategorySelect.vue';
  import V2MenuPhotoSlot from '../editor/V2MenuPhotoSlot.vue';
  import V2MenuSizesEditor from '../editor/V2MenuSizesEditor.vue';

  /**
   * The inline editor of a dish (plan §4.5.1), right below its row: name,
   * description, price or sizes, category, add-on groups, photo, allergens,
   * «Ver en la carta», «Quitar plato» and «Listo». Every field saves when it
   * is left (or at once for taps), one change per save, nothing when it did
   * not change. A field the owner is typing in keeps its text when a delta
   * replaces the dish; an untouched field is read from the server again. A
   * tap whose save is refused shows the server value again. Saves go
   * straight to the session's queue (ids only), so an add-on group saved
   * or a photo uploaded after this editor closed still lands on the dish.
   */
  const props = defineProps<{
    item: IDraftItem;
    draft: IMenuDraft;
    currency: string;
    country: string;
    /** Some dish of the draft has allergens. */
    showAllergens: boolean;
    /** The dish was read from a page of the menu («Ver en la carta»). */
    hasSource: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'see-source'): void;
    /** «Ya está en mi menú» linked it: the row picks the other editor now. */
    (e: 'relinked'): void;
    (e: 'close'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ctx = useMenuImport();
  const modals = useModal();
  const actions = useDraftActions({ country: () => props.country });
  const reducedMotion = usePreferredReducedMotion();

  const uid = nextDomId('menu-item-editor');
  const nameId = `${uid}-name`;
  const descriptionId = `${uid}-description`;
  const categoryLabelId = `${uid}-category`;
  const allergensLabelId = `${uid}-allergens`;
  const root = ref<HTMLElement | null>(null);
  let alive = true;

  const fields = useDishFields({
    item: () => props.item,
    country: () => props.country,
  });
  const { form, errors } = fields;
  const { value: categoryId, set: setCategoryValue } = useOptimisticValue(
    () => props.item.categoryId
  );
  const { value: hidden, set: setHiddenValue } = useOptimisticValue(
    () => props.item.hidden
  );
  const { value: allergens, set: setAllergensValue } = useOptimisticValue(
    () => props.item.allergens
  );
  const { value: photo, set: setPhotoValue } = useOptimisticValue(
    () => props.item.photo
  );

  const categoryOptions = computed(() =>
    orderedCategories(props.draft).map((category) => ({
      value: category.id,
      label: category.name,
    }))
  );
  const photoUploading = computed(() =>
    ctx.photos.uploading.value.has(props.item.id)
  );
  const linkedGroups = computed(() =>
    props.item.addonGroupIds
      .map((id) => props.draft.addonGroups[id])
      .filter((group): group is IDraftAddonGroup => group !== undefined)
  );
  /** The draft's groups this dish does not use yet («Usar un grupo que ya tiene»). */
  const otherGroups = computed(() =>
    Object.values(props.draft.addonGroups).filter(
      (group) => !props.item.addonGroupIds.includes(group.id)
    )
  );
  /** Shown while some dish has allergens; once shown it stays (a last chip turned off must not vanish). */
  const allergensOpen = ref(props.showAllergens || props.item.allergens !== 0);
  const sizesWide = computed(() => form.value.priceSizes.rows.length > 0);
  /** An update session with dishes of the menu left to pick («Ya está en mi menú»). */
  const canPickLive = computed(
    () => (props.draft.live?.others.length ?? 0) > 0
  );

  watch(
    () => props.showAllergens || props.item.allergens !== 0,
    (shown) => {
      if (shown) allergensOpen.value = true;
    }
  );

  function groupLine(group: IDraftAddonGroup): string {
    const rule = addonRuleKey(group);
    return `${group.title} · ${tr(rule.key, rule.params)}`;
  }

  /** One save through the session's queue; null when refused (the session told the owner). */
  function send(ops: TDraftOp[]): Promise<IDraftPatchResult | null> {
    return ctx.mutate(ops);
  }

  //#region Text fields
  function commit(field: TDishField) {
    const result = fields.commit([field]);
    if (result.status === 'valid') void send(result.ops);
  }

  function onFocus(field: TDishField) {
    fields.focusField(field);
  }

  function onBlur(field: TDishField) {
    fields.blurField(field);
    commit(field);
  }

  /** Enter in the name: leaving the field saves it. */
  function blurName() {
    const input = document.getElementById(nameId);
    if (input instanceof HTMLElement) input.blur();
  }

  function onPriceSizesFocus(inside: boolean) {
    if (inside) onFocus('priceSizes');
    else onBlur('priceSizes');
  }
  //#endregion

  //#region Taps (saved at once; a refused save puts the server value back)
  function setCategory(id: string) {
    if (id === categoryId.value) return;
    setCategoryValue(
      id,
      send([{ op: 'moveItem', itemId: props.item.id, categoryId: id }])
    );
  }

  function setHidden(value: boolean) {
    setHiddenValue(
      value,
      send([{ op: 'updateItem', itemId: props.item.id, hidden: value }])
    );
  }

  function setAllergens(bits: number) {
    setAllergensValue(
      bits,
      send([{ op: 'updateItem', itemId: props.item.id, allergens: bits }])
    );
  }

  function removePhoto() {
    setPhotoValue(
      null,
      send([{ op: 'updateItem', itemId: props.item.id, photo: null }])
    );
  }

  /** The menu flow uploads and saves it, whether or not this editor stays open. */
  function pickPhoto(file: File) {
    void ctx.photos.upload(props.item.id, file);
  }
  //#endregion

  //#region Add-on groups
  /** «Agregar grupo» (null) or «Editar»: the modal resolves the operations of its save. */
  async function openGroup(group: IDraftAddonGroup | null) {
    const ops = await modals.showDynamicDialog<TDraftOp[] | null>(
      V2MenuAddonGroupModal,
      {
        group,
        itemId: props.item.id,
        usedIn: group ? groupUsage(props.draft, group.id) : 0,
        itemPrice: referencePrice(props.item),
        currency: props.currency,
        country: props.country,
      },
      ADDON_GROUP_MODAL,
      { width: 520, clickToClose: false }
    );
    // Saved even when the editor closed meanwhile: the operations carry ids only.
    if (ops && ops.length > 0) void send(ops);
  }

  function unlinkGroup(groupId: string) {
    void send([{ op: 'unlinkAddonGroup', groupId, itemIds: [props.item.id] }]);
  }

  function linkGroup(groupId: string, close: () => void) {
    close();
    void send([{ op: 'linkAddonGroup', groupId, itemIds: [props.item.id] }]);
  }
  //#endregion

  /** «Ya está en mi menú»: the owner picks the dish of the menu it is. */
  async function pickLive() {
    const linked = await actions.pickLive(props.item.id);
    if (linked && alive) emit('relinked');
  }

  //#region Remove and close
  async function removeItem(close: () => void) {
    close();
    const itemId = props.item.id;
    const confirmed = await modals.confirm({
      title: tr('menuImport.editor.removeItemTitle'),
      message: tr('menuImport.editor.removeItemMessage', {
        name: props.item.name,
      }),
      acceptText: tr('menuImport.editor.remove'),
      cancelText: tr('cancel'),
      acceptColor: 'error',
    });
    if (!confirmed) return;
    void send([{ op: 'deleteItem', itemId }]);
  }

  /** The focused field inside the editor is left, which saves it. */
  function blurInside() {
    const active = document.activeElement;
    if (active instanceof HTMLElement && root.value?.contains(active))
      active.blur();
  }

  /** «Listo»: saves the field being typed in; stays open while a value is wrong. */
  function done() {
    blurInside();
    if (errors.value.name || errors.value.priceSizes !== null) return;
    emit('close');
  }
  //#endregion

  // Opened at the bottom of the screen, or moved to another category: bring it
  // into view; opened higher up, its end («Listo») scrolls just above the
  // floating «Pedir cambios» (the list's scroll padding).
  onMounted(async () => {
    await nextTick();
    if (!alive || !root.value) return;
    const top = root.value.getBoundingClientRect().top;
    const row = root.value.closest('[data-item-id]');
    const behavior = reducedMotion.value === 'reduce' ? 'auto' : 'smooth';
    if (row && (top < 0 || top > window.innerHeight * 0.75))
      row.scrollIntoView({ block: 'start', behavior });
    else root.value.scrollIntoView({ block: 'nearest', behavior });
  });

  onBeforeUnmount(() => {
    // Closing while typing still saves what was typed.
    blurInside();
    alive = false;
  });
</script>

<template>
  <section ref="root" class="v2-menu-item-editor" :aria-label="item.name">
    <div class="editor-grid">
      <PosUiField
        class="span-2"
        :label="$t('menuImport.editor.name')"
        :input-id="nameId"
        :error="errors.name ? $t('menuImport.editor.nameRequired') : undefined"
      >
        <PosUiInput
          :id="nameId"
          :value="form.name"
          :maxlength="DRAFT_NAME_MAX"
          autocomplete="off"
          @input="fields.setName"
          @focus="onFocus('name')"
          @blur="onBlur('name')"
          @enter="blurName"
        />
      </PosUiField>
      <button
        v-if="canPickLive"
        type="button"
        class="span-2 live-link"
        @click="pickLive"
      >
        {{ $t('menuImport.update.alreadyOnMenu') }}
      </button>
      <PosUiField
        class="span-2"
        :label="$t('menuImport.editor.description')"
        :input-id="descriptionId"
      >
        <PosUiTextarea
          :id="descriptionId"
          :value="form.description"
          :rows="3"
          :maxlength="DRAFT_DESCRIPTION_MAX"
          @input="fields.setDescription"
          @focus.native="onFocus('description')"
          @blur.native="onBlur('description')"
        />
      </PosUiField>
      <V2MenuSizesEditor
        :class="{ 'span-2': sizesWide }"
        :value="form.priceSizes"
        :errors="errors.priceSizes"
        :currency="currency"
        @input="fields.setPriceSizes"
        @focus-change="onPriceSizesFocus"
      />
      <div class="editor-side">
        <PosUiField
          :label="$t('menuImport.editor.category')"
          :label-id="categoryLabelId"
        >
          <V2MenuCategorySelect
            :value="categoryId"
            :options="categoryOptions"
            :label-id="categoryLabelId"
            :list-label="$t('menuImport.editor.category')"
            @input="setCategory"
          />
        </PosUiField>
        <PosUiSwitch
          :value="hidden"
          :label="$t('menuImport.item.hidden')"
          @input="setHidden"
        />
      </div>

      <div class="editor-block span-2">
        <p class="block-label">{{ $t('menuImport.editor.addons') }}</p>
        <ul v-if="linkedGroups.length > 0" class="group-list">
          <li v-for="group in linkedGroups" :key="group.id" class="group-row">
            <span class="group-text">{{ groupLine(group) }}</span>
            <div class="group-actions">
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="pencil-outline"
                @click="openGroup(group)"
                >{{ $t('menuImport.editor.editGroup') }}</PosUiButton
              >
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="link-variant-off"
                @click="unlinkGroup(group.id)"
                >{{ $t('menuImport.editor.unlinkGroup') }}</PosUiButton
              >
            </div>
          </li>
        </ul>
        <div class="group-add">
          <PosUiButton
            color="neutral"
            variant="outline"
            icon="plus"
            @click="openGroup(null)"
            >{{ $t('menuImport.editor.addGroup') }}</PosUiButton
          >
          <PosUiPopover v-if="otherGroups.length > 0" placement="bottom">
            <template #trigger="{ open, toggle }">
              <PosUiButton
                color="neutral"
                variant="ghost"
                icon="playlist-plus"
                aria-haspopup="true"
                :aria-expanded="open ? 'true' : 'false'"
                @click="toggle"
                >{{ $t('menuImport.editor.useExistingGroup') }}</PosUiButton
              >
            </template>
            <template #default="{ close }">
              <div class="group-menu">
                <button
                  v-for="group in otherGroups"
                  :key="group.id"
                  type="button"
                  class="group-menu-item"
                  @click="linkGroup(group.id, close)"
                >
                  {{ groupLine(group) }}
                </button>
              </div>
            </template>
          </PosUiPopover>
        </div>
      </div>

      <div class="editor-block span-2">
        <p class="block-label">{{ $t('menuImport.editor.photo') }}</p>
        <V2MenuPhotoSlot
          :value="photo"
          :uploading="photoUploading"
          @pick="pickPhoto"
          @remove="removePhoto"
        />
      </div>

      <div v-if="allergensOpen" class="editor-block span-2">
        <p :id="allergensLabelId" class="block-label">
          {{ $t('menuImport.editor.allergens') }}
        </p>
        <V2MenuAllergenChips
          :value="allergens"
          :labelled-by="allergensLabelId"
          @input="setAllergens"
        />
        <p class="block-hint">{{ $t('menuImport.editor.allergensHint') }}</p>
      </div>
    </div>

    <footer class="editor-foot">
      <PosUiButton
        v-if="hasSource"
        color="neutral"
        variant="ghost"
        icon="image-search-outline"
        @click="emit('see-source')"
        >{{ $t('menuImport.editor.seeOnMenu') }}</PosUiButton
      >
      <div class="foot-end">
        <PosUiPopover placement="top" align="end">
          <template #trigger="{ open, toggle }">
            <button
              type="button"
              class="menu-trigger"
              :aria-label="$t('menuImport.more')"
              :aria-expanded="open ? 'true' : 'false'"
              aria-haspopup="true"
              @click="toggle"
            >
              <PosUiIcon name="dots-horizontal" :size="20" />
            </button>
          </template>
          <template #default="{ close }">
            <PosUiButton
              color="error"
              variant="ghost"
              icon="delete-outline"
              block
              class="menu-item"
              @click="removeItem(close)"
              >{{ $t('menuImport.editor.remove') }}</PosUiButton
            >
          </template>
        </PosUiPopover>
        <PosUiButton color="neutral" @click="done">{{
          $t('menuImport.editor.close')
        }}</PosUiButton>
      </div>
    </footer>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-item-editor {
    @apply tw-mt-2 tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-p-4 tw-shadow-sm;

    // One column on phones, two from tablets: name, description, the add-on
    // groups, the photo and the allergens take the whole width.
    .editor-grid {
      @apply tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2;
      .span-2 {
        @apply md:tw-col-span-2;
      }
    }
    .editor-side {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    }
    .live-link {
      @apply -tw-mt-3 tw-min-h-11 tw-cursor-pointer tw-justify-self-start tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4 hover:tw-text-neutral-900;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .editor-block {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    }
    .block-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .block-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .group-list {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0;
    }
    .group-row {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-100 tw-py-1;
      &:last-child {
        @apply tw-border-b-0;
      }
    }
    .group-text {
      @apply tw-min-w-0 tw-flex-1 tw-break-words tw-text-sm tw-text-neutral-900;
      min-width: min(12rem, 100%);
    }
    .group-actions {
      @apply tw-flex tw-flex-wrap tw-gap-1;
    }
    .group-add {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;
    }
    .group-menu {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .group-menu-item {
      @apply tw-min-h-11 tw-w-full tw-cursor-pointer tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-left tw-text-sm tw-text-neutral-800 hover:tw-bg-neutral-100;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .editor-foot {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-neutral-100 tw-pt-3;
    }
    .foot-end {
      @apply tw-ml-auto tw-flex tw-items-center tw-gap-2;
    }
    .menu-trigger {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-text-neutral-600 hover:tw-bg-neutral-100;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .menu-item {
      @apply tw-justify-start;
    }
  }
</style>
